import { defineStore } from 'pinia';
import axios from 'axios';
import type {
   ApiSearchRequestParams,
   Contributor,
   Course,
   Lesson,
   CourseCategory,
   PaginatedResults,
   SearchFieldsOptions,
} from './types/types';
import useAuthStore from './stores/auth';
import {
   defaultResponseStatus,
   handleApiRequest,
} from './functions/handleApiRequest';

export type ResponseStatus = {
   statusCode: number | undefined;
   message?: string;
   error?: string;
};

interface LmsStoreState {
   accessToken: string | undefined;
   paginatedCourseCategories: PaginatedResults<CourseCategory> | undefined;
   lmsApiBaseurl: string;
   currentCourseCategory: CourseCategory | undefined;
   currentCategoryCourses: PaginatedResults<Course> | undefined;
   currentSearchedCourses: PaginatedResults<Course> | undefined;
   currentContributorCourses: PaginatedResults<Contributor> | undefined;
   currentCourse: Course | undefined;
   currentLesson: Lesson | undefined;
   currentContributor: Contributor | undefined;
   searchFieldsOptions: SearchFieldsOptions | undefined;
   appliedSearchFilters: SearchFieldsOptions | undefined;
   currentPaginatorPage: number | undefined;
   isSearchingCourses: boolean;
   isSearchingCourseCategories: boolean;
   isSearchingContributors: boolean;
   isFetchingCourse: boolean;
   isFetchingLesson: boolean;
   isFetchingContributor: boolean;
   isFetchingCourseCategory: boolean;
   isFetchingCurrentContributor: boolean; // Added to track fetching state for a single contributor
   featuredContributors: Contributor[] | undefined;
   paginatedContributors: PaginatedResults<Contributor> | undefined;
   featuredContent: (Contributor | CourseCategory)[] | undefined;
   isFetchingFeaturedContent: boolean;
   featuredContributorsFetched: boolean;
   isSendingSurvey: boolean;
   contributorsFetched: boolean;
   isFetchingProgress: boolean;

   //Response status codes and messages
   courseResponseStatus: ResponseStatus;
   lessonResponseStatus: ResponseStatus;
   contributorResponseStatus: ResponseStatus;
   contributorsResponseStatus: ResponseStatus;
   featuredContentResponseStatus: ResponseStatus;
   courseCategoryResponseStatus: ResponseStatus;
   sendSurveyResponseStatus: ResponseStatus;
   progressResponseStatus: ResponseStatus;

   // Error state properties
   sendSurveyError: string | null;
   refreshProgressError: string | null;
   refreshActivitiesError: string | null;
}

interface LmsStoreGetters {}

type LessonSurveyData = {
   url: string;
   lessonTitle: string;
   userName: string;
   userEmail: string;
   rate: number;
   difficulty_rating: string | null;
   comments: string;
   lesson_slug: string;
   lesson_name: string;
};

interface LmsStoreActions {
   /**
    * @deprecated Use `useCourseCategories()` composable instead (TanStack Query)
    * @see composables/useCourseCategories.ts
    */
   setCourseCategories: (page?: number) => void;
   /**
    * Set the active course category page after user clicks on a course category link
    * @param slug
    * @deprecated Use `useCourseCategory()` composable instead (TanStack Query)
    * @see composables/useCourseCategories.ts
    */
   setCurrentCourseCategory: (slug: string) => void;
   /**
    * Set the paginated courses for the currently selected course category
    */
   setCurrentCategoryCourses: (pageNumber?: number) => void;
   /**
    * Set the active course page after user clicks on a course link
    * @param slug
    */
   setCurrentCourse: (slug: string) => void;
   unsetCurrentCourse: () => void;
   /**
    * Set the current lesson after user clicks on a lesson link
    * @param slug
    */
   setCurrentLesson: (courseSlug: string, lessonSlug: string) => void;
   unsetCurrentLesson: () => void;

   /**
    * Set the data required to build search fields, e.g. contributors search field dropdown options
    * @deprecated Use `useSearchFieldsOptions()` composable instead (TanStack Query)
    * @see composables/useCourseSearch.ts
    */
   setSearchFieldsOptions: () => void;
   /**
    * @deprecated Use `useCourseSearch()` composable instead (TanStack Query)
    * @see composables/useCourseSearch.ts
    */
   searchCourses: (selectedFilters: SearchFieldsOptions) => void;

   /**
    * @deprecated Use `useContributors()` composable instead (TanStack Query)
    * @see composables/useContributors.ts
    */
   setContributors: (page?: number, contributorIds?: number[]) => void;

   /**
    * Fetch featured content and set them as store data
    */
   setFeaturedContent: (
      contentTypes: ('contributors' | 'courses' | 'courseCategories')[],
   ) => void;

   /**
    * Set the active contributor page after user clicks on a contributor link
    * @param slug
    * @deprecated Use `useContributor()` composable instead (TanStack Query)
    * @see composables/useContributor.ts
    */
   setCurrentContributor: (slug: string) => void;

   /**
    * @deprecated Use `useContributorCourses()` composable instead (TanStack Query)
    * @see composables/useContributor.ts
    */
   setCurrentContributorCourses: (pageNumber?: number) => void;

   submitLessonSurvey: (data: LessonSurveyData) => Promise<boolean>;

   /**
    * Update lesson progress in store
    */
   updateLessonProgress: (lessonSlug: string, progressPercent: number) => void;

   /**
    * Update course progress in store
    */
   updateCourseProgress: (courseSlug: string, progressPercent: number) => void;

   /**
    * Fetch current progress from API and update store
    */
   refreshProgressFromAPI: (
      courseSlug: string,
      lessonSlug: string,
   ) => Promise<void>;

   /**
    * Refresh activities status data for tooltips
    */
   refreshActivitiesStatus: (
      courseSlug: string,
      lessonSlug: string,
   ) => Promise<void>;
}

const useLmsStore = defineStore<string, LmsStoreState, {}, LmsStoreActions>(
   'lms',
   {
      state: (): LmsStoreState => {
         const userStore = useAuthStore();
         const { accessToken } = userStore;
         return {
            accessToken,
            paginatedCourseCategories: undefined,
            lmsApiBaseurl: '',
            currentCourseCategory: undefined,
            currentCategoryCourses: undefined,
            currentContributorCourses: undefined,
            currentCourse: undefined,
            currentContributor: undefined,
            searchFieldsOptions: undefined,
            currentSearchedCourses: undefined,
            appliedSearchFilters: undefined,
            currentPaginatorPage: undefined,
            currentLesson: undefined,
            paginatedContributors: undefined,
            featuredContributors: undefined,
            featuredContent: undefined,
            isFetchingFeaturedContent: false,
            featuredContributorsFetched: false,
            isSearchingCourses: false,
            isSearchingContributors: false,
            isSearchingCourseCategories: false,
            isFetchingCourse: false,
            isFetchingLesson: false,
            isFetchingContributor: false,
            isFetchingCourseCategory: false,
            isFetchingCurrentContributor: false,
            isSendingSurvey: false,
            contributorsFetched: false,
            isFetchingProgress: false,
            courseResponseStatus: defaultResponseStatus,
            lessonResponseStatus: defaultResponseStatus,
            contributorResponseStatus: defaultResponseStatus,
            contributorsResponseStatus: defaultResponseStatus,
            featuredContentResponseStatus: defaultResponseStatus,
            courseCategoryResponseStatus: defaultResponseStatus,
            sendSurveyResponseStatus: defaultResponseStatus,
            progressResponseStatus: defaultResponseStatus,

            // Error state properties
            sendSurveyError: null,
            refreshProgressError: null,
            refreshActivitiesError: null,
         };
      },
      actions: {
         async setCourseCategories(pageNumber) {
            if (!useAuthStore().authHeaders) {
               return;
            }

            if (this.isSearchingCourseCategories) {
               return;
            }

            // Set both loading states before the API call
            this.isSearchingCourses = true;
            this.isSearchingCourseCategories = true;

            const paginationParam =
               pageNumber === undefined ? '' : `?page=${pageNumber}`;

            try {
               const res = await axios.get(
                  `${this.lmsApiBaseurl}course-categories${paginationParam}`,
                  { headers: useAuthStore().authHeaders },
               );
               this.paginatedCourseCategories = res.data;
            } catch (error) {
               console.error('Error fetching course categories:', error);
               this.paginatedCourseCategories = undefined;
            } finally {
               this.isSearchingCourseCategories = false;
               this.isSearchingCourses = false;
            }
         },
         async setCurrentCourseCategory(slug) {
            // Prevent duplicate calls if a fetch is already in progress
            if (this.isFetchingCourseCategory) {
               return;
            }

            // Set isSearchingCourses to true before the API call
            this.isSearchingCourses = true;

            const result = await handleApiRequest(
               this,
               'isFetchingCourseCategory',
               'courseCategoryResponseStatus',
               () =>
                  axios.get(`${this.lmsApiBaseurl}course-categories/${slug}`, {
                     headers: useAuthStore().authHeaders,
                  }),
               { ref: 'currentCourseCategory', value: undefined },
            );

            if (result) {
               this.currentCourseCategory = result.data;
            }

            // Ensure these states are reset regardless of the API call result
            this.isSearchingCourses = false;
            this.isSearchingCourseCategories = false;
         },
         unsetCurrentCourse() {
            this.currentCourse = undefined;
         },
         async setCurrentCourse(slug) {
            if (this.isFetchingCourse) {
               return;
            }
            const result = await handleApiRequest(
               this,
               'isFetchingCourse',
               'courseResponseStatus',
               () =>
                  axios.get(`${this.lmsApiBaseurl}courses/${slug}`, {
                     headers: useAuthStore().authHeaders,
                  }),
               { ref: 'currentCourse', value: undefined },
            );

            if (result) {
               this.currentCourse = {
                  ...result.data,
                  should_link_to_first_lesson: Boolean(
                     result.data.should_link_to_first_lesson,
                  ),
               };
            }
         },
         unsetCurrentLesson() {
            this.lessonResponseStatus = defaultResponseStatus;
            this.currentLesson = undefined;
         },
         async setCurrentLesson(courseSlug, lessonSlug) {
            if (this.isFetchingLesson) {
               return;
            }
            const result = await handleApiRequest(
               this,
               'isFetchingLesson',
               'lessonResponseStatus',
               () =>
                  axios.get(
                     `${this.lmsApiBaseurl}courses/${courseSlug}/lessons/${lessonSlug}`,
                     {
                        headers: useAuthStore().authHeaders,
                     },
                  ),
               { ref: 'currentLesson', value: undefined },
            );

            if (result) {
               this.currentLesson = result.data;
            }
         },
         async setCurrentCategoryCourses(pageNumber = 1) {
            // Prevent duplicate calls if a fetch is already in progress
            if (this.isSearchingCourses) {
               return;
            }

            this.isSearchingCourses = true;
            this.currentCategoryCourses = undefined;

            try {
               const paginationParam =
                  pageNumber === undefined ? '' : `?page=${pageNumber}`;
               const res = await axios.get(
                  `${this.lmsApiBaseurl}course-categories/${this.currentCourseCategory?.slug}/courses${paginationParam}`,
                  { headers: useAuthStore().authHeaders },
               );
               this.currentSearchedCourses = res.data;
            } catch (error) {
               console.error('Error fetching category courses:', error);
               this.currentSearchedCourses = undefined;
            } finally {
               this.isSearchingCourses = false;
            }
         },
         async setSearchFieldsOptions() {
            // Prevent duplicate calls if a fetch is already in progress
            if (this.isSearchingContributors) {
               return;
            }

            const result = await handleApiRequest(
               this,
               'isSearchingContributors',
               'contributorsResponseStatus',
               () =>
                  axios.get(`${this.lmsApiBaseurl}options`, {
                     headers: useAuthStore().authHeaders,
                  }),
               { ref: 'searchFieldsOptions', value: undefined },
            );

            if (result) {
               this.searchFieldsOptions = result;
            }
         },
         async searchCourses(selectedFilters) {
            // Check if this is a more complete search than the current one
            const hasContributors =
               selectedFilters.contributors &&
               selectedFilters.contributors.length > 0;
            const currentHasContributors =
               this.appliedSearchFilters &&
               this.appliedSearchFilters.contributors &&
               this.appliedSearchFilters.contributors.length > 0;
            const isMoreComplete = hasContributors && !currentHasContributors;

            // Prevent duplicate calls if a fetch is already in progress, unless this search is more complete
            if (this.isSearchingCourses && !isMoreComplete) {
               return;
            }

            this.appliedSearchFilters = selectedFilters;

            // Build search parameters
            const selectedContributorIds =
               this.appliedSearchFilters.contributors?.map(
                  (contributor) => contributor.id,
               );
            const selectedCourseLevels =
               this.appliedSearchFilters.courseLevels?.map((level) => level.id);
            const selectedCourseCategories =
               this.appliedSearchFilters.courseCategories?.map(
                  (courseCategory) => courseCategory.id,
               );
            const selectedAudiences = this.appliedSearchFilters.audience?.map(
               (audience) => audience.toString(),
            );

            const params: ApiSearchRequestParams = {};

            // Use explicit page from selectedFilters if provided, otherwise default to page 1
            // Do not use currentPaginatorPage as it can persist from previous searches
            if (selectedFilters.page !== undefined) {
               params.page = selectedFilters.page;
            } else {
               params.page = 1;
            }

            this.appliedSearchFilters.freeContent = selectedFilters.freeContent;
            if (this.currentCourseCategory) {
               const categoryId = this.currentCourseCategory.id;
               params.courseCategories = [categoryId];
            }
            if (selectedContributorIds) {
               params.contributors = selectedContributorIds;
            }
            if (selectedCourseCategories) {
               params.courseCategories = selectedCourseCategories;
            }
            if (selectedCourseLevels) {
               params.courseLevels = selectedCourseLevels;
            }
            if (selectedAudiences) {
               params.audiences = selectedAudiences;
            }
            params.courseTitle = this.appliedSearchFilters.courseTitle;
            params.freeContent = this.appliedSearchFilters.freeContent;

            this.isSearchingCourses = true;
            const result = await handleApiRequest(
               this,
               'isSearchingCourses',
               'courseResponseStatus',
               () =>
                  axios.get(`${this.lmsApiBaseurl}courses`, {
                     headers: useAuthStore().authHeaders,
                     params,
                  }),
               { ref: 'currentSearchedCourses', value: undefined },
            );

            if (result) {
               this.currentSearchedCourses = result;
            }
         },
         async setContributors(pageNumber, contributorIds) {
            // If this is an initial call (no page number specified and no filters)
            if (pageNumber === undefined && !contributorIds) {
               // And contributors have already been fetched, do nothing.
               if (this.contributorsFetched) {
                  return;
               }
               // Or if an initial fetch is already in progress (isSearchingContributors is true),
               // do nothing to prevent duplicate requests.
               // handleApiRequest sets isSearchingContributors to true before the API call.
               if (this.isSearchingContributors) {
                  return;
               }
            }
            // For subsequent page loads or filtered requests, allow the request to proceed
            // even if isSearchingContributors is true

            const params: Record<string, any> = {};

            if (pageNumber !== undefined) {
               params.page = pageNumber;
            }

            if (contributorIds && contributorIds.length > 0) {
               params['contributors[]'] = contributorIds;
            }

            const result = await handleApiRequest(
               this,
               'isSearchingContributors',
               'contributorsResponseStatus',
               () =>
                  axios.get(`${this.lmsApiBaseurl}contributors`, {
                     headers: useAuthStore().authHeaders,
                     params,
                  }),
               { ref: 'paginatedContributors', value: undefined },
            );

            if (result) {
               this.paginatedContributors = result;
               // Only set contributorsFetched to true on the successful initial fetch (no pageNumber and no filters)
               if (pageNumber === undefined && !contributorIds) {
                  this.contributorsFetched = true;
               }
            }
         },
         async setFeaturedContent(contentTypes) {
            // If a fetch is already in progress, do nothing to prevent duplicate requests.
            if (this.isFetchingFeaturedContent) {
               return;
            }

            const result = await handleApiRequest(
               this,
               'isFetchingFeaturedContent',
               'featuredContentResponseStatus',
               () =>
                  axios.get(`${this.lmsApiBaseurl}featured`, {
                     headers: useAuthStore().authHeaders,
                     params: {
                        'featured[]': contentTypes,
                     },
                  }),
               { ref: 'featuredContent', value: undefined },
            );

            if (result && result.data && result.data.contributors) {
               this.featuredContributors = result.data.contributors;
               this.isFetchingFeaturedContent = false;
            }
         },
         async setCurrentContributor(slug) {
            // Clear old courses immediately to prevent showing previous contributor's courses
            this.currentSearchedCourses = undefined;

            // Prevent duplicate calls if a fetch is already in progress
            if (this.isFetchingCurrentContributor) {
               return;
            }

            const result = await handleApiRequest(
               this,
               'isFetchingCurrentContributor',
               'contributorResponseStatus',
               () =>
                  axios.get(`${this.lmsApiBaseurl}contributors/${slug}`, {
                     headers: useAuthStore().authHeaders,
                  }),
               { ref: 'currentContributor', value: undefined },
            );

            if (result) {
               this.currentContributor = result.data;
            }
         },
         async setCurrentContributorCourses(pageNumber = 1) {
            if (!this.currentContributor?.slug) {
               return;
            }
            this.isSearchingCourses = true;
            this.currentSearchedCourses = undefined;

            try {
               const paginationParam =
                  pageNumber === undefined ? '' : `?page=${pageNumber}`;
               const res = await axios.get(
                  `${this.lmsApiBaseurl}contributors/${this.currentContributor?.slug}/courses${paginationParam}`,
                  { headers: useAuthStore().authHeaders },
               );
               this.currentSearchedCourses = res.data;
            } catch (error) {
               console.error('Error fetching contributor courses:', error);
               this.currentSearchedCourses = undefined;
            } finally {
               this.isSearchingCourses = false;
            }
         },
         async submitLessonSurvey(data) {
            console.log(
               '[submitLessonSurvey] Starting survey submission with data:',
               data,
            );
            // Set isSearchingCourses to true before the API call
            this.isSendingSurvey = true;

            const result = await handleApiRequest(
               this,
               'isSendingSurvey',
               'sendSurveyResponseStatus',
               () =>
                  axios.post(`${this.lmsApiBaseurl}lesson-survey`, data, {
                     headers: useAuthStore().authHeaders,
                  }),
               { ref: 'sendSurveyError', value: null },
            );

            console.log('[submitLessonSurvey] API response:', result);
            console.log(
               '[submitLessonSurvey] sendSurveyResponseStatus:',
               this.sendSurveyResponseStatus,
            );

            // Check success based on status code (result can be empty for successful 200 responses)
            if (this.sendSurveyResponseStatus.statusCode === 200) {
               console.log(
                  '[submitLessonSurvey] Survey submitted successfully',
               );

               // Note: We can't update currentLesson here because it's a readonly TanStack Query ref
               // The component will update the cache instead
               return true; // Success
            } else {
               console.error('[submitLessonSurvey] Survey submission failed');
               return false; // Failure
            }
         },
         updateLessonProgress(lessonSlug, progressPercent) {
            // Update current lesson if it matches
            if (this.currentLesson?.slug === lessonSlug) {
               this.currentLesson.lesson_completion_percent = progressPercent;
            }

            // Update lesson in course lessons array
            const lesson = this.currentCourse?.lessons?.find(
               (l) => l.slug === lessonSlug,
            );
            if (lesson) {
               lesson.lesson_completion_percent = progressPercent;
            } else {
               console.warn('Lesson not found in course lessons array');
            }
         },
         updateCourseProgress(courseSlug, progressPercent) {
            // Update current course if it matches
            if (this.currentCourse?.slug === courseSlug) {
               this.currentCourse.course_completion_percent = progressPercent;
            } else {
               console.warn('Course slug mismatch or no current course');
            }
         },
         async refreshProgressFromAPI(courseSlug, lessonSlug) {
            // Prevent duplicate calls if a fetch is already in progress
            if (this.isFetchingProgress) {
               return;
            }

            const result = await handleApiRequest(
               this,
               'isFetchingProgress',
               'progressResponseStatus',
               () =>
                  axios.post(
                     `${this.lmsApiBaseurl}course-progress`,
                     {
                        course_slug: courseSlug,
                        lesson_slug: lessonSlug,
                     },
                     {
                        headers: useAuthStore().authHeaders,
                     },
                  ),
               { ref: 'refreshProgressError', value: null },
            );

            if (result) {
               const { lesson_completion_percent, course_completion_percent } =
                  result;

               // Update both progresses from single response
               this.updateLessonProgress(lessonSlug, lesson_completion_percent);
               this.updateCourseProgress(courseSlug, course_completion_percent);
            } else {
               console.warn('No data received from progress API');
            }
         },
         async refreshActivitiesStatus(courseSlug, lessonSlug) {
            // Prevent duplicate calls if a fetch is already in progress
            if (this.isFetchingLesson) {
               return;
            }

            const result = await handleApiRequest(
               this,
               'isFetchingLesson',
               'lessonResponseStatus',
               () =>
                  axios.get(
                     `${this.lmsApiBaseurl}courses/${courseSlug}/lessons/${lessonSlug}/activities-status`,
                     {
                        headers: useAuthStore().authHeaders,
                     },
                  ),
               { ref: 'refreshActivitiesError', value: null },
            );

            if (result && this.currentLesson) {
               // Update only the activities_status in current lesson
               this.currentLesson.activities_status = result.activities_status;
            } else {
               console.warn(
                  'No data received from activities status API or no current lesson',
               );
            }
         },
      },
   },
);

export default useLmsStore;
